import { useEffect, useMemo, useState } from "react";
import cronstrue from "cronstrue";
import { CronExpressionParser } from "cron-parser";

interface Props {
  value: string;
  onChange: (next: string) => void;
  onValidityChange?: (valid: boolean) => void;
  label?: string;
  required?: boolean;
}

interface Example {
  label: string;
  expr: string;
}

const EXAMPLES: Example[] = [
  { label: "Weekdays 7:00", expr: "0 7 * * 1-5" },
  { label: "Every 15 minutes", expr: "*/15 * * * *" },
  { label: "Hourly", expr: "0 * * * *" },
  { label: "Daily midnight", expr: "0 0 * * *" },
  { label: "Mondays 02:00", expr: "0 2 * * 1" },
  { label: "Weekend evenings 18:00", expr: "0 18 * * 6,0" },
  { label: "First of month 09:00", expr: "0 9 1 * *" },
  { label: "Every 5 min weekdays", expr: "*/5 * * * 1-5" },
];

interface ParseState {
  valid: boolean;
  description?: string;
  nextRuns?: string[];
  error?: string;
}

function parse(expr: string): ParseState {
  const trimmed = expr.trim();
  if (!trimmed) {
    return { valid: false, error: "Schedule is required" };
  }
  try {
    const it = CronExpressionParser.parse(trimmed);
    const nextRuns: string[] = [];
    for (let i = 0; i < 3; i++) {
      nextRuns.push(it.next().toDate().toLocaleString());
    }
    let description: string;
    try {
      description = cronstrue.toString(trimmed, {
        use24HourTimeFormat: true,
        verbose: false,
      });
    } catch {
      description = trimmed;
    }
    return { valid: true, description, nextRuns };
  } catch (err) {
    return {
      valid: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

export function CronInput({
  value,
  onChange,
  onValidityChange,
  label = "Schedule (cron expression)",
  required = true,
}: Props) {
  const state = useMemo(() => parse(value), [value]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    onValidityChange?.(state.valid);
  }, [state.valid, onValidityChange]);

  const showError = touched && !state.valid;
  const showDescription = state.valid;

  return (
    <div>
      <label className="label" htmlFor="cron-input">
        {label}
        {required ? <span className="text-danger"> *</span> : null}
      </label>
      <div className="flex items-center gap-2">
        <input
          id="cron-input"
          type="text"
          className="input"
          spellCheck={false}
          autoComplete="off"
          placeholder="0 7 * * 1-5"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
        />
        <span
          aria-hidden
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-sm ${
            state.valid
              ? "bg-ok-subtle text-ok-fg"
              : showError
              ? "bg-danger-subtle text-danger"
              : "bg-surface-tint text-ink-low"
          }`}
        >
          {state.valid ? "✓" : showError ? "✗" : "·"}
        </span>
      </div>

      <div className="mt-1 min-h-[1.25rem] text-sm">
        {showDescription ? (
          <span className="text-ok-fg">{state.description}</span>
        ) : showError ? (
          <span className="text-danger">{state.error}</span>
        ) : null}
      </div>

      {state.valid && state.nextRuns ? (
        <div className="mt-1 text-xs text-ink-low">
          <span className="font-medium">Next runs:</span>{" "}
          {state.nextRuns.join(" · ")}
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className="text-xs text-ink-low">
          Examples:
        </span>
        {EXAMPLES.map((ex) => (
          <button
            key={ex.expr}
            type="button"
            className="tag-chip"
            title={ex.expr}
            onClick={() => {
              onChange(ex.expr);
              setTouched(false);
            }}
          >
            <span>{ex.label}</span>
            <code className="ml-1.5 text-[0.7rem] text-ink-low">
              {ex.expr}
            </code>
          </button>
        ))}
      </div>
    </div>
  );
}
